import type { DemoScript } from "../types.js";

/**
 * The Today screen tour: complete a task from the list, pull an overdue item
 * onto today, snooze one for tomorrow. Runs the REAL TodayRoute; row anchors
 * are the agenda rows' `data-task-id`. Rollup is pinned because the script
 * calls `complete` (see types.ts).
 */
export const todayTour: DemoScript = {
  id: "today-tour",
  titleKey: "demos.title.todayTour",
  view: { kind: "today" },
  stage: { width: 960, height: 640 },
  settings: async (ctx) => {
    await ctx.stores.settings.setSubtaskRollup("two_way");
  },
  fixture: [
    // All-day/future placement keeps the same modules populated whether the
    // demo runs at 01:00 or 23:59. The one intentional overdue item is `bank`.
    { ref: "review", titleKey: "demos.fx.reviewBudget", dayOffset: 0, allDay: true, durationMin: 1440 },
    { ref: "lunch", titleKey: "demos.fx.teamLunch", dayOffset: 0, allDay: true, durationMin: 1440, isEvent: true },
    { ref: "report", titleKey: "demos.fx.draftReport", dayOffset: 1, hour: 15, durationMin: 90, deadlineOffsetDays: 2 },
    { ref: "bank", titleKey: "demos.fx.callBank", dayOffset: -1, hour: 9, durationMin: 30 },
  ],
  steps: [
    { kind: "caption", key: "demos.cap.today.intro" },
    { kind: "moveTo", target: { kind: "task", ref: "review" }, captionKey: "demos.cap.today.complete" },
    { kind: "press" },
    { kind: "release" },
    { kind: "action", run: (ctx) => ctx.stores.tasks.complete(ctx.id("review"), true) },
    { kind: "pause", ms: 900 },
    { kind: "moveTo", target: { kind: "task", ref: "bank" }, captionKey: "demos.cap.today.overdue" },
    { kind: "pause", ms: 600 },
    { kind: "press" },
    { kind: "release" },
    {
      kind: "action",
      // An all-day slot remains active through the end of today. A fixed
      // clock time could already be overdue when this tutorial is played.
      run: (ctx) => ctx.stores.tasks.edit(ctx.id("bank"), {
        due_at: ctx.at(0),
        all_day: true,
        duration_minutes: 1440,
      }),
      captionKey: "demos.cap.today.moveToday",
    },
    { kind: "pause", ms: 1000 },
    { kind: "moveTo", target: { kind: "task", ref: "report" }, captionKey: "demos.cap.today.snooze" },
    { kind: "press" },
    { kind: "release" },
    { kind: "action", run: (ctx) => ctx.stores.tasks.snooze(ctx.id("report"), ctx.at(1, 9)) },
    { kind: "pause", ms: 1200 },
  ],
};
